import React, {
  createContext, useContext, useState, useEffect, useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { ROLES, ROLE_PERMISSIONS } from '../components/RoleGuard';

const RoleContext = createContext();

export const useRoleContext = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRoleContext must be used within a RoleProvider');
  }
  return context;
};

// Global flag to prevent multiple simultaneous auth calls
let isAuthCallInProgress = false;

// Utility: Try both session (cookie) and JWT auth for user info
async function fetchUserWithAuthFallback() {
  // Quick check - if no token and no user data, don't even try
  const token = localStorage.getItem('tkn');
  const userData = localStorage.getItem('user');

  console.log('🔍 Auth check - Token:', !!token, 'UserData:', !!userData);

  // If no token and no user data, user is definitely not authenticated
  if (!token && !userData) {
    console.log('No authentication data found, skipping API call');
    return null;
  }

  // If we have user data but no token, the session might be expired
  if (!token && userData) {
    console.log('User data exists but no token, session may be expired');
    // Clear expired user data
    localStorage.removeItem('user');
    return null;
  }

  // Prevent multiple simultaneous calls
  if (isAuthCallInProgress) {
    console.log('Auth call already in progress, skipping');
    return null;
  }

  isAuthCallInProgress = true;

  try {
    console.log('🔍 Making API call to /api/v1/users/current_user');

    // Try JWT auth first (since we're using JWT from Python backend)
    if (token) {
      console.log('🔍 Trying JWT auth with token');
      const res = await fetch('/api/v1/users/current_user', {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        cache: 'no-store',
      });
      console.log('🔍 JWT auth response:', res.status);
      if (res.ok) {
        const data = await res.json();
        console.log('🔍 JWT auth successful:', data);
        return data;
      }
    }

    // Fallback: Try session-based auth
    console.log('🔍 Trying session auth');
    const res = await fetch('/api/v1/users/current_user', {
      credentials: 'include',
      cache: 'no-store',
    });
    console.log('🔍 Session auth response:', res.status);
    if (res.ok) {
      const data = await res.json();
      console.log('🔍 Session auth successful:', data);
      return data;
    }

    // If we get here, user is not authenticated - this is normal, don't log
    return null;
  } catch (error) {
    // Only log actual errors, not 401 responses
    if (error.name !== 'TypeError') {
      console.log('Error fetching user info:', error);
    }
    return null;
  } finally {
    isAuthCallInProgress = false;
  }
}

export function RoleProvider({ children }) {
  const location = useLocation();
  const lastFetchRef = useRef(0);
  const [userRole, setUserRole] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('tkn');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');

      // Optimistically set from localStorage first so nav shows correct menu immediately after login
      if (token && userData.role) {
        setUser(userData);
        setUserRole(userData.role);
      } else {
        setUserRole(null);
        setUser(null);
      }
      setLoading(true);

      const currentPath = window.location.pathname;
      const publicPages = ['/', '/signin', '/signup', '/landing', '/about', '/apps', '/v2/demo', '/v2/p'];
      const isPublicPage = publicPages.some((page) => currentPath.startsWith(page));

      if (isPublicPage && !token && !userData.role) {
        setUserRole(ROLES.USER);
        setUser({ role: ROLES.USER });
        setLoading(false);
        return;
      }

      const now = Date.now();
      if (now - lastFetchRef.current < 5000 && userData.role) {
        setUser(userData);
        setUserRole(userData.role);
        setLoading(false);
        return;
      }

      if (token || userData.role) {
        lastFetchRef.current = now;
        try {
          const data = await fetchUserWithAuthFallback();
          if (data?.role) {
            setUser(data);
            setUserRole(data.role);
            localStorage.setItem('user', JSON.stringify(data));
          } else if (userData.role) {
            setUser(userData);
            setUserRole(userData.role);
          } else {
            setUserRole(ROLES.USER);
            setUser({ role: ROLES.USER });
          }
        } catch (error) {
          if (userData.role) {
            setUser(userData);
            setUserRole(userData.role);
          } else {
            setUserRole(ROLES.USER);
            setUser({ role: ROLES.USER });
          }
        }
      } else {
        setUserRole(ROLES.USER);
        setUser({ role: ROLES.USER });
      }

      setLoading(false);
    };

    loadUser();

    const handleStorageChange = () => {
      loadUser();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userUpdated', handleStorageChange);
    };
  }, [location.pathname]);

  const hasPermission = (permission) => {
    if (!userRole) return false;
    const permissions = ROLE_PERMISSIONS[userRole] || [];
    return permissions.includes('*') || permissions.includes(permission);
  };

  const isAdmin = () => [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(userRole);

  const isManager = () => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER].includes(userRole);

  const updateUserRole = (newRole) => {
    const updatedUser = { ...user, role: newRole };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    setUserRole(newRole);

    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('userUpdated'));
  };

  // Add method to manually refresh user data
  const refreshUserData = async () => {
    const token = localStorage.getItem('tkn');
    if (!token) {
      console.log('No token found, skipping user data refresh');
      return false;
    }

    try {
      console.log('🔄 Manually refreshing user data...');
      // Use the same endpoint as above for consistency
      const response = await fetch('/api/v1/users/current_user', {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.user && data.user.role) {
          console.log('✅ User data refreshed:', data.user);
          setUser(data.user);
          setUserRole(data.user.role);
          localStorage.setItem('user', JSON.stringify(data.user));
          return true;
        }
      } else {
        console.log('User data refresh failed:', response.status);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
    return false;
  };

  const value = {
    userRole,
    user,
    loading,
    hasPermission,
    isAdmin,
    isManager,
    updateUserRole,
    refreshUserData,
    ROLES,
    ROLE_PERMISSIONS,
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
}

export default RoleContext;
